
/**
 * Module dependencies.
 */

import { media, units } from 'src/styles/utils';
import { theme } from 'styled-tools';
import { useAuth } from 'src/context/auth/context';
import { useProjectActions } from 'src/api/app/my-revigres/projects/use-project-actions';
import { useTranslate } from 'src/core/utils/translator';
import { withSession } from 'src/core/utils/session';
import Button from 'src/components/core/buttons/button';
import Container from 'src/components/core/layout/container';
import CreateProjectSidebar from 'src/components/my-revigres/projects/create-project-sidebar';
import Loading from 'src/components/core/loading';
import Metatags from 'src/components/metatags';
import MyRevigresProjectCard from 'src/components/my-revigres/core/project-card';
import RawHtml from 'src/components/core/raw-html';
import React, { ReactElement, useCallback, useState } from 'react';
import Svg from 'src/components/core/svg';
import Type from 'src/components/core/typography';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import projectsIcon from 'src/assets/svg/projects.svg';
import styled from 'styled-components';
import useUserProjects from 'src/api/app/my-revigres/projects/use-user-projects';

/**
 * `PageWrapper` styled component.
 */

const PageWrapper = styled.div`
  overflow: hidden;
  padding: calc(${theme('dimensions.navbarHeightMobile')}px + ${units(5)}) 0 ${units(13)};
  position: relative;

  ${media.min('ms')`
    padding: calc(${theme('dimensions.navbarHeight')}px + ${units(10)}) 0 ${units(25)};
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    'title'
    'empty'
    'list';
  grid-template-columns: 100%;

  ${media.min('ms')`
    grid-template-areas:
      '. title .'
      '. list  .'
      '. empty .';
    grid-template-columns: 1fr 10fr 1fr;
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: title;
  margin-bottom: ${units(6)};

  ${media.min('ms')`
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: ${units(10)};
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2).attrs({ as: 'h1' })`
  ${media.max('ms')`
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `Empty` styled component.
 */

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: empty;
  margin-bottom: ${units(3)};
  text-align: left;
  width: ${units(39)};

  ${media.min('ms')`
    margin: ${units(7)} auto 0;
  `}
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  width: max-content;
`;

/**
 * `Icon` styled component.
 */

const Icon = styled(Svg)`
  margin-bottom: ${units(3.5)};
`;

/**
 * `ListWrapper` styled component.
 */

const ListWrapper = styled.div`
  grid-area: list;
  min-height: 250px;
  position: relative;
`;

/**
 * `List` styled component.
 */

const List = styled.div`
  display: grid;
  grid-area: list;
  grid-gap: ${units(2.5)};
  grid-template-columns: 100%;

  ${media.min('xs')`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${media.min('ms')`
    grid-gap: ${units(5)};
  `}

  ${media.min('md')`
    grid-template-columns: repeat(3, 1fr);
  `}
`;

/**
 * `Projects` page.
 */

const Projects = (): ReactElement => {
  const { locale, translate } = useTranslate();
  const { token } = useAuth();
  const {
    data: projects,
    isLoading,
    isSuccess,
    refetch
  } = useUserProjects({ token });

  const isProjectsEmpty = isEmpty(projects);
  const { isCreatingProject, onCreateProject } = useProjectActions();
  const [isOpenCreateProjectSidebar, setIsOpenCreateProjectSidebar] = useState<boolean>();
  const handleCreateProject = useCallback(data => {
    onCreateProject(data).then(() => {
      setIsOpenCreateProjectSidebar(false);
      refetch();
    });
  }, [onCreateProject, refetch]);

  return (
    <>
      <Metatags pageTitle={translate('my-revigres:dashboard.menuOptions.projects')} />

      <PageWrapper>
        <Loading active={isLoading} />

        <Container>
          <Grid>
            {isSuccess && (
              <>
                <TitleWrapper>
                  <Title>
                    {translate('my-revigres:dashboard.menuOptions.projects')}
                  </Title>

                  {!isProjectsEmpty && (
                    <StyledButton
                      onClick={() => setIsOpenCreateProjectSidebar(true)}
                      variant={'fill'}
                    >
                      {translate('my-revigres:projects.createProject')}
                    </StyledButton>
                  )}
                </TitleWrapper>

                <ListWrapper>
                  <Loading active={isLoading} />

                  {!isLoading && isProjectsEmpty && (
                    <Empty>
                      <Icon
                        icon={projectsIcon}
                        size={units(3.5)}
                      />

                      <Type.H6
                        as={'p'}
                        marginBottom={units(3)}
                        small
                      >
                        <RawHtml>
                          {translate('my-revigres:projects.emptyList')}
                        </RawHtml>
                      </Type.H6>

                      <StyledButton
                        onClick={() => setIsOpenCreateProjectSidebar(true)}
                        variant={'fill'}
                      >
                        {translate('my-revigres:projects.createProject')}
                      </StyledButton>
                    </Empty>
                  )}

                  {!isProjectsEmpty && (
                    <List>
                      {map(projects, project => (
                        <MyRevigresProjectCard
                          key={project?.id}
                          locale={locale}
                          {...project}
                        />
                      ))}
                    </List>
                  )}
                </ListWrapper>
              </>
            )}
          </Grid>
        </Container>

        <CreateProjectSidebar
          isOpen={isOpenCreateProjectSidebar}
          isSubmitting={isCreatingProject}
          onClose={() => setIsOpenCreateProjectSidebar(false)}
          onSubmit={handleCreateProject}
        />
      </PageWrapper>
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export const getServerSideProps = withSession();

/**
 * Export `Projects` page.
 */

export default Projects;
